import {
  DiscountType,
  EventAttendee,
  EventTicket,
  EventTicketDiscount,
  PaymentType,
  TicketOrder,
} from '@graphql/__generated/graphql'
import { Transaction } from '../../pages/donation/[donationUrl]/index'
import currencies from '@libs/currencies'
import moment from 'moment'
import { AdditionalTicketInfo } from '@components/event/AttendeeList'

export const importAttendeesFromCSV = (CSVFile: File, readString: any) => {
  if (!CSVFile || !readString) return
  try {
    return readString(CSVFile, {
      worker: true,
    })
  } catch (error) {
    return console.error(error)
  }
}

export const convertAttendeesToCSV = (
  attendees: EventAttendee[],
  registrationFields: { name: string }[],
  userRegistrationFields: {
    email?: string
    registrationFields?: { [key: string]: string }
  }[],
  additionalTickets?: AdditionalTicketInfo[],
  eventTickets?: EventTicket[],
  eventDiscounts?: EventTicketDiscount[]
) => {
  const registrationFieldHeaders = registrationFields
    .filter((r) => !!r?.name)
    .map((r) => r.name)

  console.log({ eventTickets, eventDiscounts })

  const headers = [
    'firstName',
    'lastName',
    'profileImage',
    'invitationSentDatetime',
    'email',
    ...(additionalTickets && additionalTickets?.length > 0
      ? ['ticketRegistrar']
      : []),
    ...(eventDiscounts && eventDiscounts?.length > 0 ? ['discountCode'] : []),
    ...(eventTickets && eventTickets?.length > 0 ? ['totalPaid'] : []),
    'ticketTitle',
    ...(eventTickets && eventTickets?.length > 0 ? ['ticketType'] : []),
    'registered',
    'checkInStatus',
    'checkInDatetime',
    ...(!!registrationFields ? registrationFieldHeaders : []),
  ] as const

  let csvArr = [
    headers
      .map((h) => {
        // NOTE: Format headers to include a space between words.
        return toPascalCase(h)
          ?.replace(/(?<=.)[A-Z]/g, ' $&')
          .trim()
      })
      .join(','),
  ]

  for (let attendee of attendees) {
    let row = []
    let isGuest = false
    let discountCode
    const attendeeEventTicket = eventTickets?.find(
      (f) => f?.title?.toUpperCase() === attendee?.ticketTitle?.toUpperCase()
    )
    for (let header of headers) {
      // if (header === 'ticketTitle') {
      //   console.log({
      //     header,
      //     val: attendee[header as keyof EventAttendee],
      //     attendee,
      //   })
      // }
      let value = attendee[header as keyof EventAttendee] || ''

      if (registrationFieldHeaders.includes(header)) {
        value =
          userRegistrationFields?.find((r) => {
            if (r?.email?.toLowerCase() === attendee.email.toLowerCase())
              return r?.email?.toLowerCase() === attendee.email.toLowerCase()
          })?.registrationFields?.[header] ?? 'N/A'
      }

      if (['firstName', 'lastName'].includes(header) && !!attendee.name) {
        value = attendee['name']?.split(' ')[header === 'firstName' ? 0 : 1]
      }

      if (header === 'ticketRegistrar') {
        value =
          additionalTickets?.find(
            (order) => order.guestTicketHolder === attendee.email
          )?.primaryTicketHolder ?? attendee.email
      }

      if (header === 'discountCode') {
        const code = additionalTickets?.find(
          (f) =>
            f.guestTicketHolder === attendee.email ||
            f.primaryTicketHolder === attendee.email
        )?.discountCode
        if (code)
          discountCode = eventDiscounts?.find(
            (f) => f.code.toUpperCase() === code.toUpperCase()
          )

        value = code ?? 'N/A'
      }

      if (header === 'totalPaid') {
        const order = additionalTickets?.find(
          (order) => order.guestTicketHolder === attendee.email
        )

        let total = 0

        let ticketQuantity = 1

        isGuest =
          order?.primaryTicketHolder !== undefined &&
          order?.primaryTicketHolder !== attendee.email

        if (!!eventTickets) {
          const ticket = attendeeEventTicket ?? eventTickets[0]

          if (!!additionalTickets) {
            const ticketOrder = additionalTickets?.filter((order) => {
              // if (order[0] !== order[1] && order[1] !== attendee.email) isGuest = true
              if (order.primaryTicketHolder === attendee.email) return true
            })
            ticketQuantity = ticketOrder.length
          }

          let price = ticket.price
          if (discountCode) {
            if (discountCode.type === DiscountType.BuyOneGetOneFree) {
              ticketQuantity = Math.ceil(ticketQuantity / 2)
            }

            if (discountCode.type === DiscountType.Percentage) {
              price -= price * ((discountCode.percentage ?? 0) / 100)
            }
          }

          total = price * ticketQuantity
          // if (ticket.price > total || ticketQuantity === 0) isGuest = true
        }
        value = total.toFixed(2)
        if (isGuest) value = 'GUEST'
      }

      if (header === 'ticketType') {
        value = attendeeEventTicket?.ticketType ?? 'N/A'
      }

      if (['invitationSentDatetime', 'checkInDatetime'].includes(header)) {
        const date =
          attendee?.[
            header as keyof Pick<
              EventAttendee,
              'checkInDatetime' | 'invitationSentDatetime'
            >
          ]
        value = !!date ? moment(date).format('DD-MM-YYYY hh:mm:ss') : 'N/A'
      }

      row.push(`"${value}"`)
    }
    csvArr.push(row.join(','))
  }

  return csvArr.join('\r\n')
}

const getSymbol = (cr: string) =>
  cr ? currencies?.[cr?.toUpperCase()].symbol : '£'

const getAmount = (amount?: number | string, cr?: string) => {
  if (!amount) return ''
  const value = typeof amount === 'string' ? parseInt(amount, 10) : amount
  return `${getSymbol(cr ?? '')}${(value / 100).toFixed(2)}`
}

export const toPascalCase = (text: string) =>
  text
    ?.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
    )
    ?.map((x) => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase())
    ?.join('')

export const convertDonationToCSV = (donations: Transaction[]) => {
  const headers = [
    'amount',
    'firstName',
    'lastName',
    'donorDob',
    'currency',
    'message',
    'created',
    'datetime',
    'address',
    'coverFee',
    'donation',
    'fee',
    'giftAid',
    'selectedProgram',
    'email',
    'paymentType',
  ] as const
  let csvArr = [headers.join(',')]
  for (let donation of donations) {
    let row = []
    for (let header of headers) {
      let value = donation[header as keyof Transaction] || ''
      if (header === 'paymentType') {
        value =
          !!donation.fee && !!donation.coverFee && !!donation.donation
            ? PaymentType.Card
            : PaymentType.DirectDebit
      }
      if (['amount', 'donation', 'fee'].includes(header)) {
        value = getAmount(value, donation.currency)
      }
      row.push(`"${value}"`)
    }
    csvArr.push(row.join(','))
  }
  return csvArr.join('\r\n')
}
